import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { IconAlertTriangle } from '@tabler/icons-react';
import { usePropertyActivationUsers } from '../../../services/properties/hooks/usePropertyActivationUsers';

interface DeactivatePropertyDialogProps {
    show: boolean;
    property: { id: string; name: string } | null;
    onCancel: () => void;
    /** `unassignUserIds` = the checked users (never includes protected accounts). */
    onConfirm: (unassignUserIds: string[]) => void;
}

/**
 * Confirmation step before deactivating a property (replaces the plain confirm dialog).
 * Besides the Channex warning it lists the users assigned to the property so they can be
 * un-assigned in the same action. Everyone is checked by default; protected platform
 * accounts are shown read-only and can only be removed manually.
 */
const DeactivatePropertyDialog: React.FC<DeactivatePropertyDialogProps> = ({ show, property, onCancel, onConfirm }) => {
    const { data, isLoading, isError } = usePropertyActivationUsers(property?.id, show);
    const users = useMemo(() => data?.data ?? [], [data]);
    const removable = useMemo(() => users.filter((u) => !u.is_protected), [users]);

    const [checked, setChecked] = useState<Set<string>>(new Set());

    // Default: every removable user checked (re-seeded whenever the list (re)loads).
    useEffect(() => {
        setChecked(new Set(removable.map((u) => u.id)));
    }, [removable]);

    const allChecked = removable.length > 0 && checked.size === removable.length;

    const toggleUser = (id: string) => {
        setChecked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        setChecked(allChecked ? new Set() : new Set(removable.map((u) => u.id)));
    };

    return (
        <Modal show={show} onHide={onCancel} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <IconAlertTriangle className="text-warning" /> Deactivate this property?
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    <b>{property?.name}</b> and its room types, rate plans, and link rates will be <b>deleted on Channex</b>.
                    Local data (bookings, rates, etc.) is kept, but all OTA channel mappings in Channex will be lost and
                    must be manually re-configured after reactivation.
                </p>

                <hr />
                <h6 className="mb-1">Un-assign users from this property</h6>
                <p className="text-muted small">
                    Checked users will lose access to this property. Un-check anyone who should keep access.
                </p>

                {isLoading && <div className="text-center py-3"><Spinner animation="border" size="sm" /> Loading users...</div>}
                {isError && <Alert variant="warning" className="py-2">Could not load the user list. You can still deactivate the property; users will stay assigned.</Alert>}

                {!isLoading && !isError && users.length === 0 && (
                    <p className="text-muted small mb-0">No users are assigned to this property.</p>
                )}

                {users.length > 0 && (
                    <>
                        {removable.length > 0 && (
                            <Form.Check
                                id="deactivate-select-all"
                                type="checkbox"
                                className="mb-2 fw-semibold"
                                label={`Select all (${checked.size}/${removable.length})`}
                                checked={allChecked}
                                onChange={toggleAll}
                            />
                        )}
                        <div className="border rounded" style={{ maxHeight: 260, overflowY: 'auto' }}>
                            {users.map((user) => (
                                <div key={user.id} className="d-flex align-items-center px-3 py-2 border-bottom">
                                    <Form.Check
                                        id={`deactivate-user-${user.id}`}
                                        type="checkbox"
                                        className="me-2"
                                        checked={user.is_protected ? false : checked.has(user.id)}
                                        disabled={user.is_protected}
                                        onChange={() => toggleUser(user.id)}
                                    />
                                    <label htmlFor={`deactivate-user-${user.id}`} className={`flex-grow-1 mb-0 ${user.is_protected ? 'text-muted' : ''}`}>
                                        <div>{user.name}</div>
                                        <div className="small text-muted">{user.email}</div>
                                    </label>
                                    {user.is_protected && <Badge bg="secondary" className="text-white">Protected</Badge>}
                                </div>
                            ))}
                        </div>
                        {users.some((u) => u.is_protected) && (
                            <p className="text-muted small mt-2 mb-0">
                                Protected accounts stay assigned and can only be removed manually.
                            </p>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="danger" disabled={isLoading} onClick={() => onConfirm(Array.from(checked))}>
                    {checked.size > 0 ? `Yes, deactivate & un-assign ${checked.size} user${checked.size > 1 ? 's' : ''}` : 'Yes, deactivate'}
                </Button>
                <Button variant="primary" onClick={onCancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DeactivatePropertyDialog;
